const DEFAULT_WIDGET_LAYOUT: any = {
    gauge: {
        w: 15,
        h: 9,
    },
    common: {
        w: 20,
        h: 7,
    },
};

const ALARMS_COMMENTS_CATEGORIES = 'ALARMS';
/**
 * Shortest free-text comment the comments API will persist, mirroring the server's
 * `MIN_COMMENT_LENGTH`.
 *
 * The forms validate the *trimmed* length against this, which catches the common case of a
 * whitespace-only or one-character body and turns it into inline validation instead of a generic
 * submission failure.
 *
 * Note this is parity on trimming only. The server measures the bound after it has also stripped
 * markup, so a body that is only markup (`<b></b>`) clears the client check and is still rejected
 * server-side. Reproducing the server's sanitizer on the client to close that gap would mean
 * keeping two strip implementations in step, which is a worse trade than the rare generic error.
 */
const MIN_COMMENT_LENGTH = 2;
const UPCOMING_COMMENTS_CATEGORIES = 'UPCOMING';
const GENERAL_COMMENTS_CATEGORY = 'GENERAL';
const LOST_COMMUNICATION_COMMENTS_CATEGORY = 'LOST_COMMUNICATION';
/**
 * `categoryFilter` value that lists a device's comments from every category. An empty string
 * omits the `category` query param; leaving `categoryFilter` undefined instead lets the
 * device-comments endpoint fall back to its ALARMS default.
 */
const ALL_COMMENT_CATEGORIES_FILTER = '';

/**
 * Every category a device comment can be filed under, as persisted by the comments API.
 *
 * Mirrors the server's `CommentCategoryType` enum exactly. A value absent there is rejected
 * during request deserialization, so this list must not gain entries the API does not accept.
 * Activation comments are filed as GENERAL and have no category of their own.
 *
 * Frozen as well as `as const`: the latter is erased at compile time, so without the freeze a
 * consumer could still push onto or reassign entries of the exported array at runtime.
 */
const COMMENT_CATEGORIES = Object.freeze([
    ALARMS_COMMENTS_CATEGORIES,
    UPCOMING_COMMENTS_CATEGORIES,
    GENERAL_COMMENTS_CATEGORY,
    LOST_COMMUNICATION_COMMENTS_CATEGORY,
] as const);

/**
 * Union of the valid comment categories. Prefer this over `string` on comment props so a
 * mis-cased or misspelled value (e.g. `'Upcoming'`, `'ACTIVATON'`) fails to compile.
 */
type CommentCategory = (typeof COMMENT_CATEGORIES)[number];

/** Lookup set, so the guard needs neither a cast nor a linear scan. */
const COMMENT_CATEGORY_SET: ReadonlySet<string> = new Set<string>(COMMENT_CATEGORIES);

/**
 * Narrows an untrusted value (route param, query string, API payload) to a `CommentCategory`.
 *
 * The set is keyed by `string`, so the narrowing is driven purely by the runtime membership
 * check rather than by asserting the input is already a category.
 */
const isCommentCategory = (value?: unknown): value is CommentCategory =>
    typeof value === 'string' && COMMENT_CATEGORY_SET.has(value);

/**
 * Categories whose comments hang off a dated event and are therefore readable through an
 * event-scoped endpoint (`getTimelineComments` for ALARMS, `getUpcomingComments` for UPCOMING).
 * Every other category is recorded against the device itself and has no such endpoint.
 */
const EVENT_SCOPED_COMMENT_CATEGORIES: readonly CommentCategory[] = Object.freeze([
    ALARMS_COMMENTS_CATEGORIES,
    UPCOMING_COMMENTS_CATEGORIES,
]);

/**
 * Whether a comment view has an originating *event* to scope its list to.
 *
 * An entity id alone is not enough: hosts such as Device Details pass the device id as the
 * entity (it is the `entityId` new comments are filed under), but a device is not an event,
 * so its list must stay device-wide and unfiltered by category. Only an entity id belonging
 * to an event-scoped category can narrow the view.
 */
const hasEventScopedComments = (category: CommentCategory, entityId?: string): boolean =>
    Boolean(entityId) && EVENT_SCOPED_COMMENT_CATEGORIES.includes(category);

export type { CommentCategory };

export {
    DEFAULT_WIDGET_LAYOUT,
    ALARMS_COMMENTS_CATEGORIES,
    MIN_COMMENT_LENGTH,
    UPCOMING_COMMENTS_CATEGORIES,
    GENERAL_COMMENTS_CATEGORY,
    LOST_COMMUNICATION_COMMENTS_CATEGORY,
    ALL_COMMENT_CATEGORIES_FILTER,
    COMMENT_CATEGORIES,
    EVENT_SCOPED_COMMENT_CATEGORIES,
    hasEventScopedComments,
    isCommentCategory,
};
